import { Injectable } from '@angular/core';
import { HelpArticle } from '@app/shared/models/help-article.model';
import { BehaviorSubject, combineLatest, Observable, switchMap, throwError } from 'rxjs';
import { catchError, filter, map, startWith, tap } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { ENDPOINTS } from '@core/constants/endpoints';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class HelpService {
    private isVisible = false;
    private label$: Observable<string>;

    showPanel$: BehaviorSubject<boolean>;
    isLoading$: BehaviorSubject<boolean>;
    articles$: Observable<HelpArticle[]>;

    constructor(private router: Router, private http: HttpClient) {
        this.showPanel$ = new BehaviorSubject<boolean>(false);
        this.isLoading$ = new BehaviorSubject<boolean>(false);

        this.label$ = this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            startWith({ urlAfterRedirects: this.router.url }),
            map(() => {
                return this.router.url
                    .split('?')[0]
                    .split('/')
                    .filter((item) => !!item)
                    .join('-');
            }),
            tap(() => {
                this.isLoading$.next(true);
            })
        );

        this.articles$ = combineLatest([this.label$, this.showPanel$]).pipe(
            filter(([, showPanel]) => !!showPanel),
            switchMap(([label]) => this.getArticles(label)),
            tap(() => this.isLoading$.next(false))
        );
    }

    openPanel() {
        this.isVisible = true;
        this.isLoading$.next(this.isVisible);
        this.showPanel$.next(this.isVisible);
    }

    togglePanelVisibility(close = false) {
        this.isVisible = close ? false : !this.isVisible;
        this.isLoading$.next(this.isVisible);
        this.showPanel$.next(this.isVisible);
    }

    private getArticles(label: string) {
        return this.http.get<HelpArticle[]>(ENDPOINTS.data.help).pipe(
            map((articles) => articles.filter((value) => value.labels.includes(label))),
            catchError((error) => throwError(() => new Error(error)))
        );
    }
}
